import React, { useState, useRef, useCallback } from 'react';
import {
  Row,
  Col,
  Button,
  ButtonGroup,
  OverlayTrigger,
  Tooltip,
  Form,
} from 'react-bootstrap';
import QRCode from 'qrcode.react';
import { useSelector, useDispatch } from 'react-redux';
import { producerActions, sessionActions } from '../../redux/_actions';
import {
  sessionStatus,
  sessionRoleType,
  sessionViewType,
} from '../../redux/_constants';
import { dateTime, teamScoring } from '../../utilities/conversions';
import { DISCORD_LINK } from '../../utilities/constants';
import { useQuery } from '@apollo/client';
import { RoutineStatus } from '../../models';
import './triggerpanel.css';
import { discordIcon, downloadIcon } from '../helpers/icons';
import { useHotkeys } from 'react-hotkeys-hook';
import { useHistory } from 'react-router-dom';
import { useQueryParams, useQueryParamsAdv } from '../../utilities/query';
import { useEvalConfig } from './hooks';

import GetLineupWithRoutines from '../../apollo/queries/GetLineupWithRoutines.graphql';

function TriggerPanel({
  row = false,
  isExperimental = false,
  judgeType,
  judgeLock = false,
}) {
  const lineupIdA = useSelector((state) => state.producer.teamA.lineupId);
  const lineupLengthA = useSelector(
    (state) =>
      state.producer.teamA.lineup.filter(
        (potentialRoutine) => !!potentialRoutine.athleteId
      ).length
  );
  const lineupIdB = useSelector((state) => state.producer.teamB?.lineupId);
  const lineupLengthB = useSelector(
    (state) =>
      state.producer.teamB?.lineup?.filter(
        (potentialRoutine) => !!potentialRoutine.athleteId
      ).length
  );
  const round = useSelector((state) => state.producer.round);
  const numTeams = useSelector((state) => state.producer.teams.length);
  const streams = useSelector((state) => state.session.streams?.items);

  const { producer, session } = useSelector((state) => state);
  const dispatch = useDispatch();
  const history = useHistory();
  const basicParams = useQueryParams();
  const { queryParams, setQueryParams } = useQueryParamsAdv(
    {
      s: '',
      beta: '',
      judge: '',
      apparatus: '',
      evalslab: '',
    },
    true
  ); // using replace instead of push
  const streamType = basicParams.get('stream');
  const sessionKey = session.sessionKey;
  const [qrUrlType, setQrUrlType] = useState('scores');
  const [utmSource, setUtmSource] = useState('arena');
  const [infoPlacement, setInfoPlacement] = useState('top');
  const qrRef = useRef(null);
  const infoRef = useRef(null);
  const { CREATED, PREGAME, LIVE, POSTGAME } = sessionStatus;

  const downloadQRCode = useCallback(() => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    // Clone SVG and remove the embedded image (we'll add logo separately)
    const svgClone = svg.cloneNode(true);
    const embeddedImage = svgClone.querySelector('image');
    if (embeddedImage) embeddedImage.remove();

    const svgData = new XMLSerializer().serializeToString(svgClone);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const qrImg = new Image();
    const logoImg = new Image();

    const canvasSize = 512;
    const padding = 40;
    const logoSize = 120;

    qrImg.onload = () => {
      canvas.width = canvasSize;
      canvas.height = canvasSize;

      // White background with rounded corners effect
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvasSize, canvasSize);

      // Draw QR code with padding
      ctx.drawImage(
        qrImg,
        padding,
        padding,
        canvasSize - padding * 2,
        canvasSize - padding * 2
      );

      // Load and draw the V logo on top
      logoImg.onload = () => {
        const logoX = (canvasSize - logoSize) / 2;
        const logoY = (canvasSize - logoSize) / 2;
        ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);

        const link = document.createElement('a');
        const urlLabel =
          qrUrlType.startsWith('J') || qrUrlType.startsWith('E')
            ? `judge-${qrUrlType}`
            : qrUrlType;
        link.download = `virtius-${urlLabel}-${sessionKey}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      };

      logoImg.onerror = () => {
        // If logo fails to load, download without it
        const link = document.createElement('a');
        const urlLabel =
          qrUrlType.startsWith('J') || qrUrlType.startsWith('E')
            ? `judge-${qrUrlType}`
            : qrUrlType;
        link.download = `virtius-${urlLabel}-${sessionKey}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      };

      logoImg.src = '/v_logo.svg';
    };

    qrImg.src =
      'data:image/svg+xml;base64,' +
      btoa(unescape(encodeURIComponent(svgData)));
  }, [sessionKey, qrUrlType]);
  const { ADMIN, JUDGE, PRODUCER, FAN, COACH } = sessionRoleType;
  const evalConfig = useEvalConfig();
  const { roles, jPanel, ePanel } = evalConfig || {};
  const roleCheck = roles?.[judgeType?.toUpperCase()] || false;

  useHotkeys(
    'v',
    (e) => {
      e.preventDefault();
      handleView();
    },
    [session.view]
  );

  const { data: lineupDataA } = useQuery(GetLineupWithRoutines, {
    variables: { id: lineupIdA },
    skip: !lineupIdA,
  });

  const { data: lineupDataB } = useQuery(GetLineupWithRoutines, {
    variables: { id: lineupIdB },
    skip: !lineupIdB,
  });

  let completedRoutinesA = [];
  let completedRoutinesB = [];
  let roundComplete = false;
  if (lineupDataA && lineupDataB) {
    completedRoutinesA = lineupDataA.getLineup.routines.items.filter(
      (routine) =>
        routine.rotation === round && routine.status === RoutineStatus.COMPLETE
    );
    completedRoutinesB = lineupDataB.getLineup.routines.items.filter(
      (routine) =>
        routine.rotation === round && routine.status === RoutineStatus.COMPLETE
    );
    if (
      completedRoutinesA.length === lineupLengthA &&
      completedRoutinesB.length === lineupLengthB
    ) {
      roundComplete = round;
    }
  }

  const handleInfo = () => {
    if (infoRef.current) {
      const rect = infoRef.current.getBoundingClientRect();
      setInfoPlacement(rect.top < 350 ? 'bottom' : 'top');
    }
  };

  const handleClip = () => {
    dispatch(producerActions.clip(!producer.clip));
  };

  const handleLive = () => {
    if (producer.edit) {
      dispatch(producerActions.edit(!producer.edit));
    }
    dispatch(producerActions.live(!producer.live, roundComplete));
  };

  const handleEdit = () => {
    if (producer.live) {
      dispatch(producerActions.live(!producer.live, false));
    }
    dispatch(producerActions.edit(!producer.edit));
  };

  const handleUpload = () => {
    dispatch(producerActions.upload(!producer.upload));
  };

  const handleView = () => {
    const {
      DEFAULT,
      SOLOPLAYER,
      DUALPLAYER,
      DUALLINEUP,
      SOLOLINEUP,
      PLAYERDUAL,
      PLAYERMULTI,
      MULTIONLY,
    } = sessionViewType;
    let targetView = DEFAULT;

    switch (session.view) {
      case DEFAULT:
        if (session.role === JUDGE) {
          targetView = DUALLINEUP;
        } else {
          targetView = SOLOLINEUP;
        }
        break;
      case SOLOLINEUP:
        if (session.role === JUDGE) {
          targetView = DUALLINEUP;
        } else {
          targetView = SOLOPLAYER;
        }
        break;
      case DUALLINEUP:
        if (session.role === JUDGE) {
          targetView = SOLOLINEUP;
        } else {
          targetView = SOLOPLAYER;
        }
        break;
      case SOLOPLAYER:
        targetView = DUALPLAYER;
        break;
      case DUALPLAYER:
        targetView = PLAYERDUAL;
        break;
      case PLAYERDUAL:
        if (isExperimental) {
          targetView = MULTIONLY;
        } else if (session.role === JUDGE) {
          targetView = SOLOLINEUP;
        } else {
          targetView = DUALLINEUP;
        }
        break;
      case PLAYERMULTI:
        targetView = MULTIONLY;
        break;
      case MULTIONLY:
        targetView = PLAYERMULTI;
        break;
      default:
        targetView = DEFAULT;
        break;
    }
    dispatch(sessionActions.changeView(targetView));
  };

  // toggle undo mode to force active routine back one state
  const handleUndo = () => {
    dispatch(producerActions.undo());
  };

  const handleLeaderboard = () => {
    dispatch(producerActions.leaderboard(true));
  };

  const handleDiscord = () => {
    window.open(DISCORD_LINK, '_blank');
  };

  const handleStreams = (e, option) => {
    if (option !== null) {
      history.push(`/session?s=${sessionKey}&stream=${option}`);
    }
  };

  const undoButton = (
    <OverlayTrigger
      placement="top"
      delay={{ show: 700, hide: 0 }}
      key="undoButton"
      overlay={<Tooltip id="popover-contained">Undo On Air / On Eval</Tooltip>}
    >
      <Button
        variant={producer.undo ? 'outline-primary' : 'outline-secondary'}
        onClick={handleUndo}
        disabled={!producer.live}
      >
        UNDO
      </Button>
    </OverlayTrigger>
  );

  const getQrUrl = (type) => {
    const baseSessionUrl = `${window.location.origin}/session?s=${sessionKey}`;
    switch (type) {
      case 'session':
        return baseSessionUrl;
      case 'scores':
        return `https://scores.virti.us/?s=${sessionKey}${
          utmSource
            ? `&utm_source=${encodeURIComponent(
                utmSource
              )}&utm_medium=qr&utm_campaign=scores`
            : ''
        }`;
      case 'judge':
        return `${baseSessionUrl}&evalslab`;
      default:
        // Judge-specific URLs (J1, J2, E1, E2, etc.)
        if (type.startsWith('J') || type.startsWith('E')) {
          return `${baseSessionUrl}&evalslab&judge=${type}`;
        }
        return baseSessionUrl;
    }
  };

  const qrUrl = getQrUrl(qrUrlType);

  // Get judge roles for dropdown options from jPanel or ePanel
  const judgeRoles = jPanel
    ? jPanel.map((j) => j.type)
    : ePanel
    ? ePanel.map((e) => e.type)
    : [];
  const showEvalslabOptions = session.role === ADMIN || session.role === JUDGE;

  const infoButton = (
    <OverlayTrigger
      placement={infoPlacement}
      delay={{ show: 700, hide: 0 }}
      arrowProps={false}
      trigger="click"
      key="infoButton"
      rootClose
      overlay={
        <Tooltip id="popover-contained" className="sessionInfo">
          <p className="title">{session?.name}</p>
          <p className="date">{dateTime(session?.startAt, true)}</p>
          {session?.location && <p className="location">{session.location}</p>}
          <p className="teamscoring">{`Team: ${teamScoring(
            session?.teamScoring
          )}`}</p>
          <div className="qrSection">
            <div
              ref={qrRef}
              className="qrWrapper"
              onClick={() => window.open(qrUrl, '_blank')}
              style={{ cursor: 'pointer' }}
            >
              <QRCode
                className="sessionQRcode"
                value={qrUrl}
                size={200}
                bgColor="none"
                fgColor="#5E5E5E"
                level="H"
                renderAs="svg"
                imageSettings={{
                  src: '/v_logo.svg',
                  x: undefined,
                  y: undefined,
                  height: 48,
                  width: 48,
                  excavate: true,
                }}
              />
            </div>
            <div className="qrControls">
              <Form.Control
                as="select"
                className="qrUrlSelector"
                value={qrUrlType}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setQrUrlType(e.target.value)}
              >
                <option value="session">virti.us</option>
                <option value="scores">scores.virti.us</option>
                {showEvalslabOptions && (
                  <>
                    <option value="judge">Judge</option>
                    {judgeRoles.map((role) => (
                      <option key={role} value={role}>
                        Judge {role}
                      </option>
                    ))}
                  </>
                )}
              </Form.Control>
              <Button
                className="qrDownloadBtn"
                variant="link"
                onClick={(e) => {
                  e.stopPropagation();
                  downloadQRCode();
                }}
              >
                {downloadIcon}
              </Button>
            </div>
            {qrUrlType === 'scores' && (
              <Form.Control
                className="utmSourceInput"
                type="text"
                placeholder="utm source (e.g. arena)"
                value={utmSource}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) =>
                  setUtmSource(e.target.value.replace(/\s/g, '_'))
                }
              />
            )}
          </div>
        </Tooltip>
      }
    >
      <Button
        ref={infoRef}
        className="infoButton"
        variant="outline-secondary"
        onClick={handleInfo}
        disabled={false}
      >
        INFO
      </Button>
    </OverlayTrigger>
  );

  const editButton = (
    <OverlayTrigger
      placement="top"
      delay={{ show: 700, hide: 0 }}
      overlay={<Tooltip id="popover-contained">Modify Lineups</Tooltip>}
      key="editButton"
    >
      <Button
        variant={producer.edit ? 'outline-primary' : 'outline-secondary'}
        onClick={handleEdit}
        //disabled={producer.live} // will tie later in with producer mode
      >
        EDIT
      </Button>
    </OverlayTrigger>
  );

  const viewButton = (
    <OverlayTrigger
      placement="top"
      delay={{ show: 700, hide: 0 }}
      key="viewButton"
      overlay={<Tooltip id="popover-contained">Change View</Tooltip>}
    >
      <Button variant="outline-secondary" onClick={handleView} disabled={false}>
        VIEW
      </Button>
    </OverlayTrigger>
  );

  const streamButton = (
    <OverlayTrigger
      placement="top"
      delay={{ show: 700, hide: 0 }}
      key="streamButton"
      rootClose
      overlay={<Tooltip id="popover-contained">Streams</Tooltip>}
    >
      <ButtonGroup>
        {streams.map((s, i) => {
          return (
            <Button
              key={i}
              className={
                streamType === '' + i || (streamType === null && i === 0)
                  ? 'selectedStream'
                  : null
              }
              variant="outline-secondary"
              onClick={(e) => handleStreams(e, i)}
              disabled={
                streamType === '' + i || (streamType === null && i === 0)
              }
            >
              {s.title}
            </Button>
          );
        })}
      </ButtonGroup>
    </OverlayTrigger>
  );

  const clipButton = (
    <OverlayTrigger
      placement="top"
      delay={{ show: 700, hide: 0 }}
      key="clipButton"
      overlay={
        <Tooltip id="popover-contained">Mark Routine Start/Finish</Tooltip>
      }
    >
      <Button
        variant={producer.clip ? 'outline-success' : 'outline-secondary'}
        onClick={handleClip}
      >
        CLIP
      </Button>
    </OverlayTrigger>
  );

  const uploadButton = (
    <OverlayTrigger
      placement="top"
      delay={{ show: 700, hide: 0 }}
      key="uploadButton"
      overlay={<Tooltip id="popover-contained">Upload Videos</Tooltip>}
    >
      <Button
        variant={producer.upload ? 'outline-success' : 'outline-secondary'}
        onClick={handleUpload}
      >
        UPLD
      </Button>
    </OverlayTrigger>
  );

  const liveButton = (
    <OverlayTrigger
      placement="top"
      delay={{ show: 700, hide: 0 }}
      key="liveButton"
      overlay={
        <Tooltip id="popover-contained">
          {!producer.live ? 'Start Scoring' : 'End Scoring'}
        </Tooltip>
      }
    >
      <Button
        variant={producer.live ? 'outline-danger' : 'outline-secondary'}
        onClick={handleLive}
        disabled={session.status !== LIVE} // || producer.edit}
      >
        {session.status === LIVE
          ? producer.live
            ? 'STOP'
            : 'START'
          : numTeams === 3
          ? 'TRI'
          : numTeams === 4
          ? 'QUAD'
          : session.type}
      </Button>
    </OverlayTrigger>
  );

  const leaderboardButton = (
    <OverlayTrigger
      placement="top"
      delay={{ show: 700, hide: 0 }}
      overlay={<Tooltip id="popover-contained">Show Leaderboard</Tooltip>}
      key="leaderboardButton"
      rootClose
    >
      <Button variant="outline-secondary" onClick={handleLeaderboard}>
        STATS
      </Button>
    </OverlayTrigger>
  );

  // const eventScoreButton = (
  //   <OverlayTrigger
  //     placement="top"
  //     delay={{ show: 700, hide: 0 }}
  //     overlay={<Tooltip id="popover-contained">Show Leaderboard</Tooltip>}
  //     key="leaderboardButton"
  //     rootClose
  //   >
  //     <Button variant="outline-secondary" onClick={handleLeaderboard}>
  //       {VS}
  //     </Button>
  //   </OverlayTrigger>
  // );

  const discordButton = (
    <OverlayTrigger
      placement="top"
      delay={{ show: 700, hide: 0 }}
      overlay={<Tooltip id="popover-contained">Join Discord</Tooltip>}
      key="discordButton"
    >
      <Button
        className="discordButton"
        variant="outline-secondary"
        onClick={handleDiscord}
      >
        {discordIcon} CHAT
      </Button>
    </OverlayTrigger>
  );

  const adminPanelRow = [
    infoButton,
    session.status === POSTGAME ? null : liveButton,
    leaderboardButton,
    viewButton,
    streamButton,
    editButton,
    session.status === LIVE ? undoButton : null,
    session.status === POSTGAME ? clipButton : null,
    [LIVE, POSTGAME].includes(session.status) ? uploadButton : null,
  ];

  const userPanelRow = [
    infoButton,
    // viewButton,
    streamButton,
    leaderboardButton,
    discordButton,
  ];

  const judgePanelRow = [
    judgeLock ? null : infoButton,
    session.status === LIVE && (!judgeType || !judgeType.match(/^J[2-9]$/))
      ? liveButton
      : null,
    //session.status === LIVE ? null : viewButton,
    //viewButton,
    !roleCheck ? leaderboardButton : null,
    [LIVE, PREGAME].includes(session.status) && !roleCheck ? editButton : null,
    session.status === LIVE && (!judgeType || !judgeType.match(/^J[2-9]$/))
      ? undoButton
      : null,
  ];

  const panelSwitcherRow = () => {
    switch (session.role) {
      case ADMIN:
        return adminPanelRow;
      case JUDGE:
        return judgePanelRow;
      case PRODUCER:
      case COACH:
      default:
        return userPanelRow;
    }
  };

  const userType = () => {
    //return "FAN";
    switch (session.role) {
      case ADMIN:
        return ADMIN;
      case PRODUCER:
        return PRODUCER;
      case COACH:
        return COACH;
      case JUDGE:
        if (roleCheck) {
          return `${JUDGE}: ${judgeType.toUpperCase()}`;
        } else if (isExperimental) {
          return judgeTypeSelector();
        } else {
          return JUDGE;
        }
      default:
        return FAN;
    }
  };

  const sessionStatusLabel = () => {
    switch (session.status) {
      case CREATED:
      case PREGAME:
        return 'PRE';
      case LIVE:
        return 'LIVE';
      case POSTGAME:
        return 'POST';
      default:
        return 'POST';
    }
  };

  const sessionStatusButton = () => {
    let options = {
      CREATED: 'CREATED',
      PREGAME: 'PRE',
      LIVE: 'LIVE',
      POSTGAME: 'POST',
    };

    return (
      <Form.Control
        as="select"
        label="Status"
        id="admin-session-status-menu"
        value={session.status}
        onClick={(e) => {
          e.stopPropagation();
        }}
        onChange={(e) => {
          const payload = {
            ...session,
            status: e.target.value,
          };
          dispatch(sessionActions.updateStatus(payload));
        }}
      >
        {Object.keys(options).map((statusType) => {
          return (
            <option key={statusType} value={statusType}>
              {options[statusType]}
            </option>
          );
        })}
      </Form.Control>
    );
  };

  const judgeTypeSelector = () => {
    let options = roles || {};

    if (!roles) {
      return null;
    }

    return (
      <Form.Control
        as="select"
        label="JudgeType"
        id="admin-session-judgetype-menu"
        onClick={(e) => {
          e.stopPropagation();
        }}
        defaultValue="Judge"
        onChange={(e) => {
          dispatch(producerActions.live(false, false));
          if (producer.edit) {
            dispatch(producerActions.edit(false));
          }
          setQueryParams({
            ...queryParams,
            judge: e.target.value,
          });
        }}
      >
        <option value="Judge">JUDGE</option>
        {Object.keys(options).map((judgeType) => {
          return (
            <option key={judgeType} value={judgeType}>
              {judgeType}
            </option>
          );
        })}
      </Form.Control>
    );
  };

  const triggerPanelRow = (
    <div className="triggerPanelRow">
      <Row className="tPanelRowStyle">
        <Col className="tPanelStart vCenter" xs={1}>
          <span
            className={[
              'vCenter',
              producer.live ? 'blinkingLiveText' : null,
            ].join(' ')}
          >
            {session.role === ADMIN
              ? sessionStatusButton()
              : sessionStatusLabel()}
          </span>
        </Col>
        <Col xs={10}>
          <div className="tPanelRowButtons">{panelSwitcherRow()}</div>
        </Col>
        <Col className="tPanelEnd vCenter" xs={1}>
          <span className="vCenter">{userType()}</span>
        </Col>
      </Row>
    </div>
  );

  return triggerPanelRow;
}

export default TriggerPanel;
